import type * as React from "react";
import { cn } from "../../lib/cn";

/**
 * VendorPreviewShell — DRY layout primitive for every vendor preview
 * card mounted inside the ``FocusedActionView`` body slot.
 *
 * Before this primitive, every preview (Email / Calendar / FiC /
 * Document*) re-implemented the same 4 layout zones (header banner,
 * field grid, footer hint, optional banner). After May 25, 2026 SOTA
 * pass: every preview is `<VendorPreviewShell …>` with structured
 * children. Single source of truth for spacing, type scale, hairline
 * rhythm, dark-mode contrast tokens.
 *
 * Zones (top → bottom):
 *
 *   1. **icon + title + caption**  — the vendor banner. Renders an
 *      orange icon badge, a 14px medium title, and an optional 11.5px
 *      ink-3 caption (entity / channel / counter-party).
 *   2. **fields grid**             — 1- or 2-column field strip
 *      (children pass ``<VendorPreviewField label value />`` rows).
 *   3. **bottomBanner**            — optional emphasis card (e.g.
 *      "Importo: €123,45 · Scadenza 30/06/2026") with semantic color.
 *   4. **footnote**                — micro caption rendered at the
 *      bottom (e.g. "Verrà firmata digitalmente via SDI Acube").
 *
 * No store reads, no router, no business logic — pure layout. Vendor-
 * specific components compose around this.
 */

export interface VendorPreviewShellProps {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly caption?: React.ReactNode;
  /**
   * Optional bottom emphasis banner (semantic colour band).
   * ``tone="accent"`` = ando-orange-soft (default); ``tone="warning"`` =
   * amber band; ``tone="info"`` = blue band; ``tone="success"`` =
   * emerald band; ``tone="danger"`` = rose band.
   */
  readonly bottomBanner?: {
    readonly label: string;
    readonly value: React.ReactNode;
    readonly tone?: "accent" | "warning" | "info" | "success" | "danger";
  };
  readonly footnote?: React.ReactNode;
  /**
   * Optional full-width rich body rendered as its own hairline-separated band
   * inside the sheet (e.g. an email / message editor). Kept borderless by the
   * caller (``<RichEmailBody bare>``) so it reads as one continuous letter.
   */
  readonly body?: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly className?: string;
}

// Semantic emphasis-band tints (background + text only — the band's separation
// comes from the sheet's own hairline border-top).
const TONE_CLASS: Record<NonNullable<NonNullable<VendorPreviewShellProps["bottomBanner"]>["tone"]>, string> = {
  accent: "bg-ando-orange-soft/30 text-ink-1",
  warning: "bg-amber-50 text-amber-900",
  info: "bg-sky-50 text-sky-900",
  success: "bg-emerald-50 text-emerald-900",
  danger: "bg-rose-50 text-rose-900",
};

/**
 * SOTA (Jul 2026): ONE continuous "sheet" — header · fields · body · banner as
 * hairline-divided rows sharing a single border + focus-ring — instead of the
 * old stacked ``rounded-xl`` cards ("spezzato"). Mirrors the unified email
 * compose. Every preview that renders through this primitive inherits the look.
 */
export const VendorPreviewShell: React.FC<VendorPreviewShellProps> = ({
  icon,
  title,
  caption,
  bottomBanner,
  footnote,
  body,
  children,
  className,
}) => {
  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          "flex flex-col rounded-xl border border-hairline bg-white/60 transition-colors focus-within:border-ando-orange/30 focus-within:ring-2 focus-within:ring-ando-orange/40",
          className,
        )}
      >
        {/* Header row */}
        <div className="flex items-start gap-3 px-4 py-3">
          <span
            aria-hidden
            className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-md bg-ando-orange-soft text-ando-orange"
          >
            {icon}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-[14px] text-ink-1">{title}</div>
            {caption ? (
              <div className="mt-0.5 truncate text-[11.5px] text-ink-3">{caption}</div>
            ) : null}
          </div>
        </div>

        {/* Fields */}
        {children ? (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 border-hairline border-t px-4 py-3 sm:grid-cols-2">
            {children}
          </dl>
        ) : null}

        {/* Rich body (email / message editor) */}
        {body ? <div className="border-hairline border-t">{body}</div> : null}

        {/* Bottom emphasis band */}
        {bottomBanner ? (
          <div
            className={cn(
              "flex items-center justify-between gap-3 border-hairline border-t px-4 py-2.5",
              TONE_CLASS[bottomBanner.tone ?? "accent"],
            )}
          >
            <span className="font-medium text-[10px] uppercase tracking-[0.06em] opacity-80">
              {bottomBanner.label}
            </span>
            <span className="font-semibold text-[13px]">{bottomBanner.value}</span>
          </div>
        ) : null}
      </div>

      {/* Footnote — below the sheet, a muted caption */}
      {footnote ? (
        <p className="px-1 text-[11px] text-ink-3 leading-relaxed">{footnote}</p>
      ) : null}
    </div>
  );
};
VendorPreviewShell.displayName = "VendorPreviewShell";

// ── Field row primitive ──────────────────────────────────────────────────

export interface VendorPreviewFieldProps {
  readonly label: string;
  readonly value: React.ReactNode;
  /** When true the field spans both columns of the grid. */
  readonly wide?: boolean;
  /** Hide when value is falsy. */
  readonly hideEmpty?: boolean;
}

export const VendorPreviewField: React.FC<VendorPreviewFieldProps> = ({
  label,
  value,
  wide = false,
  hideEmpty = false,
}) => {
  if (hideEmpty && (value === null || value === undefined || value === "")) {
    return null;
  }
  return (
    <div className={cn("min-w-0", wide && "sm:col-span-2")}>
      <dt className="mb-0.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
        {label}
      </dt>
      <dd className="text-[13px] text-ink-1 leading-snug">
        {value ?? <span className="text-ink-3 italic">—</span>}
      </dd>
    </div>
  );
};
VendorPreviewField.displayName = "VendorPreviewField";
